import { useId } from "react";
import { cn } from "@/lib/utils";

interface PulseLoaderProps {
  /** Rendered size of the mark in pixels. */
  size?: number;
  /** Accessible label announced to screen readers. Defaults to "Loading". */
  ariaLabel?: string;
  className?: string;
}

/**
 * OpZero pulsing mark, used as the loading indicator across chat surfaces.
 *
 * Mirrors the canonical brand animation in `@opzero/ui` `src/brand/icon.svg`:
 * a dashed cyan-to-violet ring marqueeing around a white core that breathes.
 * Duplicated here because this SPA does not depend on `@opzero/ui`.
 */
export function PulseLoader({ size = 16, ariaLabel = "Loading", className }: PulseLoaderProps) {
  // Gradient ids are document-global; scope them so multiple loaders can coexist.
  const gradientId = `oz-pulse-grad-${useId().replace(/:/g, "")}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={cn("shrink-0", className)}
    >
      <style>{`
@keyframes oz-pulse-dash { to { stroke-dashoffset: -46; } }
@keyframes oz-pulse-core {
  0%, 100% { opacity: 0.5; transform: scale(0.85); filter: blur(1px); }
  50% { opacity: 1; transform: scale(1.2); filter: blur(0px) drop-shadow(0 0 8px #00F5FF) drop-shadow(0 0 14px #8B5CF6); }
}
.oz-pulse-ring { animation: oz-pulse-dash 4s linear infinite; stroke-linecap: round; }
/* The brand mark's core is white, which vanishes on the light theme. Inherit
   the surrounding text color so it reads on both; the ring carries the brand. */
.oz-pulse-core { fill: currentColor; animation: oz-pulse-core 2s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
@media (prefers-reduced-motion: reduce) {
  .oz-pulse-ring, .oz-pulse-core { animation: none; }
}
      `}</style>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00F5FF" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
      </defs>
      <circle
        cx="50"
        cy="50"
        r="42"
        stroke={`url(#${gradientId})`}
        strokeWidth="6"
        strokeDasharray="8 15"
        className="oz-pulse-ring"
      />
      <circle cx="50" cy="50" r="8" className="oz-pulse-core" />
    </svg>
  );
}

export default PulseLoader;
